import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export function useAppSettings() {
  return useQuery({
    queryKey: ["app_settings"],
    queryFn: async () => {
      // No key filter here on purpose. The anon RLS policy on app_settings is
      // key-filtered (#681, public.is_public_app_setting), so an unauthenticated
      // caller already receives only the public keys; a signed-in staff user
      // gets the full map, which the admin screens need. Duplicating the
      // allow-list in TS would be a third copy to drift. RootLayout invalidates
      // this query on SIGNED_IN/SIGNED_OUT so the two shapes can't be confused.
      const { data, error } = await supabase.from("app_settings").select("key, value");
      if (error) throw error;
      const map: Record<string, string> = {};
      for (const row of data) map[row.key] = row.value;
      return map;
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useSetting(key: string, fallback = "") {
  const { data } = useAppSettings();
  return data?.[key] ?? fallback;
}

export function useUpsertSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (settings: Record<string, string>) => {
      const rows = Object.entries(settings).map(([key, value]) => ({ key, value }));
      const { error } = await supabase.from("app_settings").upsert(rows, { onConflict: "key" });
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["app_settings"] }),
  });
}
